import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { formatCurrency } from "../lib/utils";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, Heart, ArrowRight, Share2 } from "lucide-react";

export default function DonationSuccessPage() {
  const [searchParams] = useSearchParams();
  const amount = searchParams.get("amount") || "100";
  const project = searchParams.get("project") || "Humanitarian Project";

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 flex items-center justify-center bg-[#F4F5F7]">
      <div className="absolute inset-0 hero-glow opacity-20 pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg relative z-10"
      >
        <Card className="bg-white border-slate-200 overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-br from-[#6B21A8] to-[#3B82F6] p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-white" />
              </div>
            </motion.div>
            <h1 className="font-heading font-bold text-2xl text-white mb-2">
              Thank You For Your Generosity!
            </h1>
            <p className="text-white/80">Your donation has been received</p>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Amount */}
            <div className="text-center py-4 rounded-xl bg-slate-50 border border-slate-100">
              <p className="text-sm text-slate-500 mb-1">Donation Amount</p>
              <p className="text-4xl font-heading font-black text-primary">
                ${parseFloat(amount).toLocaleString()}
              </p>
              <Badge variant="outline" className="mt-2 text-xs bg-green-50 text-green-600 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            </div>

            {/* Project Info */}
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-100">
              <p className="text-xs text-slate-400 mb-1">Project Supported</p>
              <p className="font-medium text-slate-800">{project}</p>
            </div>

            {/* Impact message */}
            <div className="text-center">
              <Heart className="h-6 w-6 text-primary mx-auto mb-2" />
              <p className="text-sm text-slate-500">
                Your contribution is making a real difference in the lives of people who need it most.
                Every dollar is tracked and verified on our blockchain audit trail.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button asChild className="w-full bg-[#6B21A8] hover:bg-[#581C87] text-white" data-testid="view-projects-btn">
                <Link to="/projects">
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Explore More Projects
                </Link>
              </Button>
              <Button variant="outline" asChild className="w-full border-slate-200" data-testid="view-dashboard-btn">
                <Link to="/public-dashboard">
                  View Live Dashboard
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
